import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import type { HeartbeatItem, WarningItem } from '@shared/lib/api/types';
import { getArmPositions } from '@/lib/arm-api';
import { useRobotStore } from '@/stores/useRobotStore';

// Red = critical, must be addressed by the user (not dismissable).
// Orange = informational; the user can hide them. They re-surface when the
// underlying condition clears server-side and re-fires (fresh timestamp).
type Severity = 'red' | 'orange';

type WarningAction = {
  label: string;
  pendingLabel: string;
  run: () => Promise<void>;
};

type Spec = {
  text: string | ((heartbeats: HeartbeatItem[]) => string);
  severity: Severity;
  action?: WarningAction;
};

const SEVERITY_CLASSES: Record<Severity, { border: string; icon: string }> = {
  red:    { border: 'border-red-500',    icon: 'text-red-500' },
  orange: { border: 'border-orange-400', icon: 'text-orange-400' },
};

const SPECS: Record<string, Spec> = {
  UNSAFE_ARM: {
    text: 'One or both arms are in an unsafe position, please manually move them to the home position.',
    severity: 'red',
    action: {
      label: 'Recheck arm positions',
      pendingLabel: 'Checking…',
      run: getArmPositions,
    },
  },
  MISSING_BASE: {
    text: 'Base is not detected — navigation will not be available. Is it turned on?',
    severity: 'orange',
  },
  OFFLINE_EVAL_MODE: {
    text: 'The robot is in offline-eval mode (its control services are stopped for testing). Arms cannot start until an engineer restores live mode.',
    severity: 'red',
  },
  ARM_CONTROLLERS_DOWN: {
    text: 'The arm controller service is not running — arms cannot start. Try restarting the robot; if it persists, contact support.',
    severity: 'red',
  },
  ISSUE_DURING_NAVIGATION: {
    text: 'Navigation interrupted by a system issue',
    severity: 'orange',
  },
  ISSUE_DURING_TASK_EXECUTION: {
    text: 'Task interrupted by a system issue',
    severity: 'orange',
  },
  ISSUE_DURING_ORCHESTRATOR_CONTROL: {
    text: 'Orchestrator run interrupted by a system issue',
    severity: 'orange',
  },
  FAULT_HOLD: {
    text: 'A base, lidar or camera fault holds the robot. A running policy is paused: the arms hold their pose and the grippers keep their grip. The base stays frozen. When the fault clears, resume or stop the policy.',
    severity: 'red',
  },
};

const DISMISS_STORAGE_KEY = 'verne.dismissed-warnings';

type DismissMap = Record<string, number>;

function loadDismissals(): DismissMap {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object') return {};
    const out: DismissMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'number') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function saveDismissals(map: DismissMap): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage unavailable (private mode, quota) — dismissal won't persist
  }
}

interface WarningStackProps {
  warnings: WarningItem[];
}

export function WarningStack({ warnings }: WarningStackProps) {
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [dismissals, setDismissals] = useState<DismissMap>(loadDismissals);
  // Sticky cache for warnings with requires_dismiss=true. They keep rendering
  // after they leave the heartbeat — only the user dismissing un-renders them.
  const [sticky, setSticky] = useState<Record<string, WarningItem>>({});

  useEffect(() => {
    setSticky((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const w of warnings) {
        if (!w.requires_dismiss) continue;
        const existing = next[w.id];
        if (existing == null || w.timestamp > existing.timestamp) {
          next[w.id] = w;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [warnings]);

  // Combined set: heartbeat takes priority (newest timestamp), sticky fills in
  // anything that's left the heartbeat but hasn't been dismissed.
  const combined = useMemo(() => {
    const seen = new Set<string>();
    const out: WarningItem[] = [];
    for (const w of warnings) {
      seen.add(w.id);
      out.push(w);
    }
    for (const w of Object.values(sticky)) {
      if (!seen.has(w.id)) out.push(w);
    }
    return out;
  }, [warnings, sticky]);

  // Garbage-collect dismissal entries whose warning is no longer surfaced
  // anywhere (heartbeat or sticky), so a future re-fire isn't accidentally
  // suppressed by an even-older `dismissed_at`.
  useEffect(() => {
    const activeIds = new Set(combined.map((w) => w.id));
    const stale = Object.keys(dismissals).filter((id) => !activeIds.has(id));
    if (stale.length === 0) return;
    setDismissals((prev) => {
      const next = { ...prev };
      for (const id of stale) delete next[id];
      saveDismissals(next);
      return next;
    });
  }, [combined, dismissals]);

  const visible = combined.filter((w) => {
    const spec = SPECS[w.id];
    if (spec == null) return false;
    if (spec.severity !== 'orange') return true;
    const dismissedAt = dismissals[w.id];
    return dismissedAt == null || dismissedAt < w.timestamp;
  });

  if (visible.length === 0) return null;

  const dismiss = (w: WarningItem) => {
    setDismissals((prev) => {
      const next = { ...prev, [w.id]: w.timestamp };
      saveDismissals(next);
      return next;
    });
    // Drop from sticky too. If the warning re-fires later, the heartbeat
    // pathway will re-populate sticky with a fresh timestamp.
    setSticky((prev) => {
      if (!(w.id in prev)) return prev;
      const next = { ...prev };
      delete next[w.id];
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-2 w-[280px]">
      {visible.map((w) => {
        const spec = SPECS[w.id];
        const classes = SEVERITY_CLASSES[spec.severity];
        const isPending = pending[w.id] === true;
        const error = errors[w.id];
        const dismissable = spec.severity === 'orange';

        const onAction = async () => {
          if (!spec.action || isPending) return;
          setPending((p) => ({ ...p, [w.id]: true }));
          setErrors((e) => ({ ...e, [w.id]: null }));
          try {
            await spec.action.run();
          } catch (err) {
            setErrors((e) => ({ ...e, [w.id]: err instanceof Error ? err.message : String(err) }));
          } finally {
            setPending((p) => ({ ...p, [w.id]: false }));
          }
        };

        return (
          <div
            key={w.id}
            className={`relative rounded-xl bg-surface/80 backdrop-blur-sm border-2 ${classes.border} p-3 flex flex-col gap-2`}
          >
            {dismissable && (
              <button
                onClick={() => dismiss(w)}
                aria-label="Dismiss warning"
                className="absolute top-1.5 right-1.5 rounded p-1 text-foreground/60 hover:text-foreground hover:bg-surface-strong transition"
              >
                <X size={14} />
              </button>
            )}
            <div className={`flex items-start gap-2 ${dismissable ? 'pr-5' : ''}`}>
              <AlertTriangle size={16} className={`shrink-0 mt-0.5 ${classes.icon}`} />
              <p className="text-xs leading-snug text-foreground">
                {typeof spec.text === 'function' ? spec.text(heartbeats) : spec.text}
              </p>
            </div>
            {spec.action && (
              <button
                onClick={onAction}
                disabled={isPending}
                className="rounded-lg border border-border bg-surface-muted px-3 py-1.5 text-xs font-medium text-foreground hover:bg-surface-strong transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? spec.action.pendingLabel : spec.action.label}
              </button>
            )}
            {error && <p className="text-[11px] text-danger leading-snug">{error}</p>}
          </div>
        );
      })}
    </div>
  );
}
